import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin } from 'lucide-react';
import PublicNav from '../components/PublicNav';
import PublicFooter from '../components/PublicFooter';
import usePageMeta from '../utils/usePageMeta';

export default function About() {
  usePageMeta(
    'About Tactmark | Football & Soccer Analysis Software',
    'Tactmark is football and soccer analysis software with two products: Post-Match for coaching decisions after a match, and Federation for technical development oversight across programs.'
  );
  return (
    <div className="min-h-screen bg-white">
      <PublicNav />

      {/* Hero */}
      <div className="max-w-3xl mx-auto px-6 pt-16 pb-4">
        <p className="text-navy-600 text-xs font-semibold tracking-widest uppercase mb-4">About Tactmark</p>
        <h1 className="font-serif text-4xl sm:text-5xl text-navy-900 leading-tight mb-8">
          Coaching decisions after the match. Technical development across the organization.
        </h1>
        <div className="space-y-5 text-gray-700 text-lg leading-relaxed">
          <p>
            Tactmark Post-Match gives coaching staff a structured decision record from completed-match
            evidence: what decided the game, the decisive risk, and the training priority for the next
            session. Tactmark Federation gives football associations a separate system for development
            objectives, program reporting, unresolved priorities, and leadership-ready technical records.
          </p>
        </div>
      </div>

      {/* Why it exists */}
      <div className="max-w-3xl mx-auto px-6 py-14 border-t border-gray-100 mt-10">
        <p className="text-navy-600 text-xs font-semibold tracking-widest uppercase mb-4">Why Tactmark exists</p>
        <h2 className="font-serif text-2xl sm:text-3xl text-navy-900 leading-snug mb-6">
          Football decisions become difficult when evidence, actions, and responsibility are fragmented.
        </h2>
        <div className="space-y-5 text-gray-600 leading-relaxed">
          <p>At team level, post-match conclusions are often scattered across notes, clips, messages, and staff meetings.</p>
          <p>At organization level, development objectives, program reports, assigned actions, and leadership decisions are often distributed across spreadsheets, documents, and individual staff members.</p>
          <p>Tactmark treats these as two distinct responsibilities — and provides a product for each.</p>
        </div>
      </div>

      {/* Two products */}
      <div className="bg-navy-900">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <p className="text-navy-400 text-xs font-semibold tracking-widest uppercase mb-4">Two products</p>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="border border-white/15 rounded p-7">
              <p className="text-navy-400 text-xs font-semibold tracking-widest uppercase mb-3">For coaching teams</p>
              <h2 className="font-serif text-2xl text-white mb-4">Tactmark Post-Match</h2>
              <p className="text-navy-100 text-sm leading-relaxed mb-6">Review a completed match, organize submitted evidence, confirm the findings that matter, decide what should change, assign a training response, and review the outcome in later matches.</p>
              <Link to="/products/post-match" className="text-white font-medium hover:underline">Explore Tactmark Post-Match</Link>
            </div>
            <div className="border border-white/15 rounded p-7">
              <p className="text-navy-400 text-xs font-semibold tracking-widest uppercase mb-3">For football organizations</p>
              <h2 className="font-serif text-2xl text-white mb-4">Tactmark Federation</h2>
              <p className="text-navy-100 text-sm leading-relaxed mb-6">Define technical-development objectives, organize programs and participating teams, establish reporting standards, oversee unresolved priorities and assigned actions, preserve institutional knowledge, and produce evidence-supported leadership reports.</p>
              <Link to="/products/federation" className="text-white font-medium hover:underline">Explore Tactmark Federation</Link>
            </div>
          </div>
        </div>
      </div>

      {/* Who it serves */}
      <div className="max-w-6xl mx-auto px-6 py-16 border-t border-gray-200">
        <p className="text-navy-600 text-xs font-semibold tracking-widest uppercase mb-8">For world football</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-start">
          <div className="space-y-5 text-gray-600 leading-relaxed">
            <p>
              Tactmark serves professional clubs, national teams, football federations, academies
              and development programs, coaching and performance departments, and independent analysts.
            </p>
            <p>
              Post-Match and Federation are designed for organizations across different countries,
              competition levels, and staff structures — without forcing one product into the other&apos;s job.
            </p>
            <p>
              Post-Match gives one coaching team a structured completed-match decision record.
              Federation gives technical leadership a separate organization-level workflow for
              objectives, program submissions, accountability, and development reporting.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              'Professional clubs',
              'National teams',
              'Football federations',
              'Academies and development programs',
              'Coaching and performance departments',
              'Independent analysts'
            ].map((item) => (
              <div key={item} className="border border-gray-200 rounded-lg px-4 py-3 text-sm font-medium text-navy-900">
                {item}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* What Tactmark is / is not */}
      <div className="bg-gray-50 border-t border-gray-200">
        <div className="max-w-6xl mx-auto px-6 py-16 grid grid-cols-1 md:grid-cols-2 gap-12">
          <div>
            <p className="text-navy-600 text-xs font-semibold tracking-widest uppercase mb-6">What Tactmark is</p>
            <ul className="space-y-3">
              {[
              'Post-Match coaching decisions from submitted match evidence',
              'Federation technical-development oversight across programs',
              'A durable record of decisions, responsibilities, actions, and reviews',
              'Evidence-aware reporting for coaching and technical leadership',
              'Separate products for team-level and organization-level responsibility'
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 text-gray-700 text-sm leading-relaxed">
                  <span className="text-navy-600 font-bold mt-0.5 flex-shrink-0">+</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="text-navy-600 text-xs font-semibold tracking-widest uppercase mb-6">What Tactmark is not</p>
            <ul className="space-y-3">
              {[
                'Automatic full-match video analysis',
                'Player-registration software',
                'Competition-management software',
                'A scouting marketplace',
                'A generic statistics dashboard',
                'A replacement for coaching or technical-leadership judgment'
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 text-gray-700 text-sm leading-relaxed">
                  <span className="text-navy-600 font-bold mt-0.5 flex-shrink-0">-</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* US-based company */}
      <div className="max-w-6xl mx-auto px-6 py-16 border-t border-gray-200">
        <p className="text-navy-600 text-xs font-semibold tracking-widest uppercase mb-8">The company</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          <div>
            <h2 className="font-serif text-2xl text-navy-900 mb-4">US-based. Serving teams and football organizations worldwide.</h2>
            <div className="space-y-4 text-gray-600 leading-relaxed">
              <p>
                Tactmark is developed in the United States for clubs, academies, national programs,
                federations, coaches, and analysts across world football.
              </p>
              <p>
                Our infrastructure, data processing, and engineering operate from the United States,
                under US law, with a transparent legal framework governing all client data.
              </p>
              <p>
                Tactmark uses contracted service providers to host, process, deliver, and support
                the products as described in the Privacy Policy. Customer data is not sold or used
                to train external systems.
              </p>
            </div>
          </div>
          <div className="space-y-6">
            <div className="flex items-start gap-4">
              <MapPin className="w-5 h-5 text-navy-700 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-navy-900 font-semibold mb-1">Headquarters</p>
                <p className="text-gray-500 text-sm">United States</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center mt-0.5">
                <div className="w-2 h-2 rounded-full bg-navy-700" />
              </div>
              <div>
                <p className="text-navy-900 font-semibold mb-1">Data residency</p>
                <p className="text-gray-500 text-sm leading-relaxed">
                  Product data is processed using US-based service infrastructure. Authorized
                  service providers process data only to deliver the service; customer data is not sold.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center mt-0.5">
                <div className="w-2 h-2 rounded-full bg-navy-700" />
              </div>
              <div>
                <p className="text-navy-900 font-semibold mb-1">Privacy &amp; Contact</p>
                <p className="text-gray-500 text-sm">
                  <a href="mailto:privacy@tactmark.com" className="text-navy-700 hover:underline">privacy@tactmark.com</a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-20 text-center border-t border-gray-200">
        <h2 className="font-serif text-3xl text-navy-900 mb-8">Choose the Tactmark product that matches your responsibility.</h2>
        <div className="flex items-center justify-center gap-4 flex-wrap">
          <Link
            to="/products/post-match"
            className="inline-block bg-navy-900 hover:bg-navy-800 text-white font-medium px-8 py-3 rounded transition-colors"
          >
            Explore Post-Match
          </Link>
          <Link
            to="/products/federation"
            className="inline-block border border-navy-900 text-navy-900 font-medium px-8 py-3 rounded hover:bg-navy-50 transition-colors"
          >
            Explore Federation
          </Link>
        </div>
        <div className="flex items-center justify-center gap-6 flex-wrap mt-5 text-sm">
          <Link to="/login?mode=signup" className="text-navy-900 hover:underline">Start Post-Match free trial</Link>
          <Link to="/contact?plan=federation&from=about" className="text-navy-900 hover:underline">Book a Federation Demo</Link>
        </div>
      </div>

      <PublicFooter />
    </div>
  );
}
